import { Component, Inject, OnDestroy, Optional } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MortgageDashboardHandover } from '../../../models/mortgage-dashboard';
import { MortgagesService } from '../../../modules/mortgages/mortgages.service';
import {
  SelectApplicationLenderService,
  fixHandoverId
} from '../../../modules/mortgages/select-application-lender/select-application-lender.service';
import { TaskApiService, Task } from '../../../modules/task/task-api.service';
import { TaskService } from '../../../modules/task/task.service';
import { fadeAnimation, sidepaneMove } from '../../../shared/animations/sidepane-animation';
import { setControlEnabled } from '../../../utils/functions/set-control-enabled';
import { SelectUserService } from '../select-user/select-user.service';
import { ToastService } from '../../../../../../projects/client/src/app/shared/services/toast.service';
import { MortgageApiService } from '../../../services/mortgage-api.service';
import { RemortgageDashboardService } from '../../../modules/remortgage/pages/remortgage-dashboard/remortgage-dashboard.service';
import { FormFieldService } from '../../../services/form-field.service';
import { ADD_TASK_SERVICE, IAddTaskService } from './tokens/add-task.tokens';
import { UserApiService } from '../../../services/user-api.service';

@Component({
  selector: 'hf-add-task',
  templateUrl: './add-task.component.html',
  styleUrls: ['./add-task.component.scss', '../../../styles/components/sidepanes.partial.scss'],
  animations: [sidepaneMove, fadeAnimation],
  host: { '[@sidepaneMove]': 'true' },
  providers: [SelectApplicationLenderService, FormFieldService]
})
export class AddTaskComponent implements OnDestroy {
  public form = this.fb.group({
    assignee: [null, Validators.required],
    summary: [null, Validators.required],
    description: [null, Validators.required],
    dueDate: [null, Validators.required],
    _handover: [null]
  });

  public isLoading = false;
  public isSubmitted = false;
  public isSubmitting = false;
  public handover: MortgageDashboardHandover;

  public get taskId() {
    return this.route.snapshot.paramMap.get('taskId');
  }

  public get value() {
    return this.form.value;
  }

  public get handovers() {
    return this.mortgagesService.data?.handovers;
  }

  public control(name: string) {
    return this.form.get(name);
  }

  private task: Task;

  private get isMortgage() {
    return !!this.route.snapshot.data.isMortgage;
  }

  private destroy$ = new Subject();

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private selectUserService: SelectUserService,
    private toasterService: ToastService,
    @Inject(ADD_TASK_SERVICE) private addTaskService: IAddTaskService,
    private taskApiService: TaskApiService,
    private taskService: TaskService,
    private mortgagesService: MortgagesService,
    private mortgageApiService: MortgageApiService,
    private selectApplicationLenderService: SelectApplicationLenderService,
    private formFieldService: FormFieldService,
    private userApiService: UserApiService,
    @Optional() private remortgageDashboardService: RemortgageDashboardService
  ) {
    this.selectUserService.userSelected$.pipe(takeUntil(this.destroy$)).subscribe((userSelected) => {
      this.form.get('assignee').setValue(userSelected);
    });

    this.selectApplicationLenderService.application$.pipe(takeUntil(this.destroy$)).subscribe((handover) => {
      this.selectApplicationLenderService.selectedHandover = handover?.id ? handover : null;
      this.control('_handover').setValue(handover?.id ? handover : null);
    });

    setControlEnabled(this.control('_handover'), this.isMortgage);

    this.taskId && this.initialize();
  }

  private async initialize() {
    this.isLoading = true;

    try {
      this.task = await this.taskApiService.viewTask(this.taskId);

      setControlEnabled(this.control('_handover'), !!this.task.mortgageId);

      this.form.patchValue(this.task);
      this.task.application && this.control('_handover').setValue(this.task.application);

      if (this.isMortgage) {
        this.selectApplicationLenderService.handovers = await this.mortgageApiService.getMortgageHandovers(
          this.task.mortgageId
        );
      }
    } catch (e) {
      this.toasterService.add({ header: 'Error' }, 'error');
    }

    this.isLoading = false;
  }

  public onSelectLender() {
    this.router.navigate(['selectApplication'], { relativeTo: this.route });
  }

  public onSelectAssigneeClicked() {
    this.selectUserService.initial = this.form.get('assignee').value;
    this.selectUserService.users$ = this.userApiService.getHeronUsers(this.route.snapshot.params);

    this.router.navigate(['selectUser'], { relativeTo: this.route });
  }

  public async onSubmitClicked() {
    this.isSubmitted = true;

    if (!this.formFieldService.checkFormValidity(this.form)) return;

    this.isSubmitting = true;

    const value = fixHandoverId(this.value);

    try {
      this.taskId
        ? await this.taskApiService.editTask(value, this.taskId)
        : await this.taskApiService.createTask(value, this.route.snapshot.paramMap);

      this.taskService.reloadTasks$.next();
      this.taskId && this.taskService.reloadCurrentTask$.next();
      this.mortgagesService.reloadMortgageDashboard.next();
      this.remortgageDashboardService && this.remortgageDashboardService.refreshJournal$.next();

      this.toasterService.add({ header: 'Task saved' }, 'success');

      this.onBack();
    } catch (err) {
      console.log(err);
      this.toasterService.add({ header: `Failed to save task. Please try again.` }, 'error');
    } finally {
      this.isSubmitting = false;
    }
  }

  public ngOnDestroy() {
    this.destroy$.next();
  }

  public onBack() {
    this.router.navigate(['..'], { relativeTo: this.route });
  }
}
